import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Print-optimized invoice view - LANDSCAPE FORMAT
 * Matches the original PDF layout
 * Opens in new tab, automatically triggers print dialog
 */
export default function PrintInvoice() {
  const { id } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [logoDataUrl, setLogoDataUrl] = useState(null);
  const [logoLoading, setLogoLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [invRes, settingsRes] = await Promise.all([
          axios.get(`${API}/invoices/${id}`),
          axios.get(`${API}/settings`)
        ]);
        setInvoice(invRes.data);
        
        // Settings is returned as an object
        const settingsData = settingsRes.data || {};
        const logoUrl = settingsData.logo_url || "";
        
        setSettings({
          company_name: settingsData.company_name || "",
          company_logo: logoUrl,
          company_address: settingsData.company_address || "",
          company_phone: settingsData.company_phone || ""
        });
        
        // Fetch logo as blob and convert to data URL to avoid CORS issues
        if (logoUrl) {
          setLogoLoading(true);
          try {
            const logoResponse = await fetch(logoUrl);
            const blob = await logoResponse.blob();
            const reader = new FileReader();
            reader.onloadend = () => {
              setLogoDataUrl(reader.result);
              setLogoLoading(false);
            };
            reader.onerror = () => {
              setLogoLoading(false);
            };
            reader.readAsDataURL(blob);
          } catch (logoErr) {
            console.error("Failed to load logo:", logoErr);
            setLogoLoading(false);
          }
        }
      } catch (err) {
        setError("Failed to load invoice");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchData();
    }
  }, [id]);

  // Auto-trigger print dialog when loaded (wait for logo if exists)
  useEffect(() => {
    if (invoice && !loading) {
      // If there's a logo URL, wait for logo to load before printing
      const hasLogo = settings.company_logo;
      const logoReady = !hasLogo || logoDataUrl || !logoLoading;
      
      if (logoReady) {
        setTimeout(() => {
          window.print();
        }, 300);
      }
    }
  }, [invoice, loading, settings.company_logo, logoDataUrl, logoLoading]);

  if (loading) {
    return (
      <div style={{ padding: "40px", textAlign: "center", fontFamily: "Arial, sans-serif" }}>
        Loading invoice...
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div style={{ padding: "40px", textAlign: "center", fontFamily: "Arial, sans-serif", color: "#dc2626" }}>
        {error || "Invoice not found"}
      </div>
    );
  }

  // Calculate totals
  const items = invoice.items || [];
  const partsTotal = items.reduce((sum, item) => 
    sum + ((item.parts_qty || 0) * (item.parts_price || 0)), 0);
  const laborTotal = items.reduce((sum, item) => 
    sum + ((item.labor_qty || 0) * (item.labor_price || 0)), 0);
  const roadFee = invoice.road_service_fee || 0;
  const storageFee = invoice.storage_fee || 0;
  const subtotal = partsTotal + laborTotal + roadFee + storageFee;
  const taxAmount = partsTotal * ((invoice.tax_rate || 0) / 100);
  const total = subtotal + taxAmount;

  const vehicle = invoice.vehicle_info || {};
  const isEstimate = invoice.status === "estimate";
  const isPaid = invoice.status === "paid";
  const docType = isEstimate ? "Estimate" : "Invoice";
  const docColor = isEstimate ? "#F59E0B" : "#2563EB";

  // Filter items with data
  const partsItems = items.filter(item => item.parts_description || item.parts_qty > 0);
  const laborItems = items.filter(item => item.labor_description || item.labor_qty > 0);

  return (
    <>
      {/* Print-specific styles - LANDSCAPE */}
      <style>{`
        @media print {
          @page {
            size: letter landscape;
            margin: 0.3in 0.4in;
          }
          body {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .no-print {
            display: none !important;
          }
        }
        
        * {
          box-sizing: border-box;
        }
        
        body {
          margin: 0;
          padding: 0;
          font-family: Arial, Helvetica, sans-serif;
          font-size: 9px;
          line-height: 1.3;
          color: #1e293b;
          background: #fff;
        }
        
        .print-container {
          width: 10.2in;
          max-width: 100%;
          margin: 0 auto;
          padding: 15px;
        }
        
        /* Header Row: Logo | Customer | Vehicle | Invoice# */
        .header-row {
          display: grid;
          grid-template-columns: 2in 2.8in 3.5in 1.9in;
          gap: 8px;
          margin-bottom: 8px;
          padding-bottom: 8px;
          border-bottom: 2px solid #2563eb;
        }
        
        .logo-cell {
          display: flex;
          align-items: flex-start;
        }
        
        .logo {
          max-height: 65px;
          max-width: 100%;
          object-fit: contain;
        }
        
        .info-section {
          font-size: 9px;
        }
        
        .section-title {
          font-size: 9px;
          font-weight: bold;
          color: #2563eb;
          margin-bottom: 4px;
        }
        
        .info-row {
          margin-bottom: 2px;
        }
        
        .info-label {
          font-weight: bold;
        }
        
        .invoice-section {
          text-align: right;
        }
        
        .invoice-type {
          font-size: 11px;
          font-weight: bold;
          color: ${docColor};
        }
        
        .invoice-details {
          font-size: 8px;
          margin-top: 4px;
        }
        
        /* Tables Container - Side by Side */
        .tables-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
          margin-bottom: 10px;
        }
        
        .table-section {
          border: 1px solid #d1d5db;
        }
        
        .table-header {
          padding: 5px 8px;
          font-weight: bold;
          color: white;
          font-size: 9px;
          text-transform: uppercase;
        }
        
        .parts-header {
          background: #2563eb;
        }
        
        .labor-header {
          background: #475569;
        }
        
        table {
          width: 100%;
          border-collapse: collapse;
          font-size: 8px;
        }
        
        th {
          background: #f1f5f9;
          padding: 4px 6px;
          text-align: left;
          font-weight: bold;
          color: #475569;
          border-bottom: 1px solid #d1d5db;
          font-size: 8px;
        }
        
        th.right, td.right {
          text-align: right;
        }
        
        td {
          padding: 4px 6px;
          border-bottom: 1px solid #e5e7eb;
          vertical-align: top;
        }
        
        .totals-row {
          background: #f8fafc;
          font-weight: bold;
        }
        
        .totals-row td {
          border-bottom: none;
        }
        
        /* Summary Section */
        .summary-row {
          display: grid;
          grid-template-columns: 1fr 280px;
          gap: 20px;
          margin-bottom: 10px;
        }
        
        .fees-section {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
          font-size: 8px;
        }
        
        .fee-item {
          display: flex;
          justify-content: space-between;
          padding: 3px 0;
        }
        
        .summary-box {
          border: 1px solid #d1d5db;
          font-size: 9px;
        }
        
        .summary-line {
          display: flex;
          justify-content: space-between;
          padding: 5px 10px;
          border-bottom: 1px solid #e5e7eb;
        }
        
        .summary-line:last-child {
          border-bottom: none;
        }
        
        .total-line {
          background: #2563eb;
          color: white;
          font-weight: bold;
          font-size: 11px;
        }
        
        /* Signature Section */
        .signature-row {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 30px;
          margin-top: 15px;
          padding-top: 10px;
        }
        
        .signature-box {
          border-top: 1px solid #1e293b;
          padding-top: 5px;
          font-size: 8px;
          color: #64748b;
        }
        
        /* Disclaimer */
        .disclaimer {
          margin-top: 12px;
          padding: 8px;
          background: #f8fafc;
          border: 1px solid #e5e7eb;
          font-size: 7px;
          color: #64748b;
          line-height: 1.4;
        }
        
        /* Print Button */
        .print-button {
          position: fixed;
          top: 15px;
          right: 15px;
          background: #2563eb;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 6px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.15);
          z-index: 1000;
        }
        
        .print-button:hover {
          background: #1d4ed8;
        }
        
        .empty-row {
          color: #94a3b8;
          text-align: center;
          font-style: italic;
        }
      `}</style>

      <div className="print-container">
        {/* Print button (hidden when printing) */}
        <button className="print-button no-print" onClick={() => window.print()}>
          🖨️ Print Invoice
        </button>

        {/* Header Row: Logo | Customer Info | Vehicle Info | Invoice# */}
        <div className="header-row">
          {/* Logo */}
          <div className="logo-cell">
            {logoDataUrl ? (
              <img 
                src={logoDataUrl} 
                alt="Company Logo" 
                className="logo"
              />
            ) : logoLoading ? (
              <div style={{ width: '120px', height: '60px', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '8px', color: '#94a3b8', borderRadius: '4px' }}>
                Loading logo...
              </div>
            ) : settings.company_logo ? (
              /* Fallback: try direct URL if blob conversion failed */
              <img 
                src={settings.company_logo} 
                alt="Company Logo" 
                className="logo"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            ) : null}
          </div>
          
          {/* Customer Info */}
          <div className="info-section">
            <div className="section-title">CUSTOMER INFO</div>
            <div className="info-row"><span className="info-label">Name:</span> {invoice.customer_name || "-"}</div>
            <div className="info-row"><span className="info-label">Phone:</span> {invoice.customer_phone || "-"}</div>
            <div className="info-row"><span className="info-label">Address:</span> {invoice.customer_address || "-"}</div>
            <div className="info-row"><span className="info-label">2nd Contact:</span> {invoice.second_contact || "-"}</div>
          </div>
          
          {/* Vehicle Info */}
          <div className="info-section">
            <div className="section-title">VEHICLE / JOB DETAILS</div>
            <div className="info-row">
              <span className="info-label">Truck #:</span> {vehicle.truck_number || "-"} | 
              <span className="info-label"> Trailer #:</span> {vehicle.trailer_number || "-"} | 
              <span className="info-label"> Make:</span> {vehicle.make || "-"}
            </div>
            <div className="info-row">
              <span className="info-label">Model:</span> {vehicle.type_model || "-"} | 
              <span className="info-label"> Year:</span> {vehicle.year || "-"} | 
              <span className="info-label"> VIN:</span> {vehicle.serial_vin || "-"}
            </div>
            <div className="info-row">
              <span className="info-label">License:</span> {vehicle.license_number || "-"} | 
              <span className="info-label"> Odometer:</span> {vehicle.odometer || "-"} | 
              <span className="info-label"> Written By:</span> {vehicle.order_written_by || "-"}
            </div>
          </div>
          
          {/* Invoice Info */}
          <div className="info-section invoice-section">
            <div className="invoice-type">{docType} #{invoice.invoice_number}</div>
            <div className="invoice-details">
              <div><span className="info-label">Received:</span> {vehicle.received_date || "-"}</div>
              <div><span className="info-label">Promised:</span> {vehicle.promised_date || "-"}</div>
              <div><span className="info-label">Phone Ready:</span> {vehicle.phone_when_ready ? "Yes" : "No"}</div>
            </div>
          </div>
        </div>

        {/* Parts & Labor Tables - Side by Side */}
        <div className="tables-row">
          {/* Parts Table */}
          <div className="table-section">
            <div className="table-header parts-header">PARTS</div>
            <table>
              <thead>
                <tr>
                  <th style={{ width: "8%" }}>Qty</th>
                  <th style={{ width: "52%" }}>Description</th>
                  <th style={{ width: "20%" }} className="right">Price</th>
                  <th style={{ width: "20%" }} className="right">Total</th>
                </tr>
              </thead>
              <tbody>
                {partsItems.length > 0 ? partsItems.map((item, idx) => (
                  <tr key={`parts-${idx}`}>
                    <td>{item.parts_qty || 0}</td>
                    <td>{item.parts_description || "-"}</td>
                    <td className="right">${(item.parts_price || 0).toFixed(2)}</td>
                    <td className="right">${((item.parts_qty || 0) * (item.parts_price || 0)).toFixed(2)}</td>
                  </tr>
                )) : (
                  <tr><td colSpan="4" className="empty-row">No parts</td></tr>
                )}
                <tr className="totals-row">
                  <td colSpan="2">Tax ({invoice.tax_rate || 0}%): ${taxAmount.toFixed(2)}</td>
                  <td className="right">Total:</td>
                  <td className="right">${partsTotal.toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Labor Table */}
          <div className="table-section">
            <div className="table-header labor-header">LABOR</div>
            <table>
              <thead>
                <tr>
                  <th style={{ width: "8%" }}>Qty</th>
                  <th style={{ width: "52%" }}>Description</th>
                  <th style={{ width: "20%" }} className="right">Price</th>
                  <th style={{ width: "20%" }} className="right">Total</th>
                </tr>
              </thead>
              <tbody>
                {laborItems.length > 0 ? laborItems.map((item, idx) => (
                  <tr key={`labor-${idx}`}>
                    <td>{item.labor_qty || 0}</td>
                    <td>{item.labor_description || "-"}</td>
                    <td className="right">${(item.labor_price || 0).toFixed(2)}</td>
                    <td className="right">${((item.labor_qty || 0) * (item.labor_price || 0)).toFixed(2)}</td>
                  </tr>
                )) : (
                  <tr><td colSpan="4" className="empty-row">No labor</td></tr>
                )}
                <tr className="totals-row">
                  <td colSpan="2"></td>
                  <td className="right">Total:</td>
                  <td className="right">${laborTotal.toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Fees & Summary */}
        <div className="summary-row">
          {/* Fees */}
          <div className="fees-section">
            <div className="fee-item">
              <span className="info-label">Road Service Fee:</span>
              <span>${roadFee.toFixed(2)}</span>
            </div>
            <div className="fee-item">
              <span className="info-label">Storage Fee:</span>
              <span>${storageFee.toFixed(2)}</span>
            </div>
          </div>
          
          {/* Summary Box */}
          <div className="summary-box">
            <div className="summary-line">
              <span>Subtotal:</span>
              <span>${subtotal.toFixed(2)}</span>
            </div>
            <div className="summary-line">
              <span>Tax ({invoice.tax_rate || 0}% on parts):</span>
              <span>${taxAmount.toFixed(2)}</span>
            </div>
            <div className="summary-line total-line">
              <span>TOTAL DUE:</span>
              <span>${total.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Signature Section */}
        <div className="signature-row">
          <div className="signature-box">Customer Signature</div>
          <div className="signature-box">Date</div>
          <div className="signature-box">Authorized By</div>
        </div>

        {/* Disclaimer */}
        <div className="disclaimer">
          <strong>DISCLAIMER:</strong> The above prices are only quotes or estimates and are subject to change 
          upon inspection and/or completion of work. Customer is responsible for costs. By signing, the customer 
          authorizes repairs and acknowledges that the company is not responsible for loss or damage due to 
          circumstances beyond our control. Liability is limited to the cost of repair performed. Third-party 
          parts warranties are provided by the manufacturer only. Items left more than 30 days become property 
          of the shop. Customer is responsible for all collection fees if account becomes delinquent.
        </div>
      </div>
    </>
  );
}
